import {
  type Grant,
  type GrantId,
  GrantsIds,
  UserFocusedGrantsRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import type { Grants } from "@storytell/types";
import { createResource } from "solid-js";

export type GrantsService = ReturnType<typeof createGrantsService>;

const defaultGrants = () =>
  GrantsIds.reduce(
    (acc, id) => {
      acc[id] = {
        "op.create": false,
        "op.delete": false,
        "op.read": false,
        "op.update": false,
      };
      return acc;
    },
    {} as Record<GrantId, Grants>,
  );

export const createGrantsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
}) => {
  const client = useStorytellClient();

  const [grantsData, { refetch, mutate }] = createResource(
    () => deps.authReady() && deps.userId(),
    async () => {
      const request = UserFocusedGrantsRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      if (response.isErr()) {
        console.error("Failed to fetch grants sync", response.error);
        // Assume no permissions by default
        return defaultGrants();
      }

      return response.value?.grants
        ? {
            ...response.value.grants,
          }
        : defaultGrants();
    },
    {
      initialValue: defaultGrants(),
      deferStream: true,
    },
  );

  // This is used to trigger a suspense boundary so SSR works properly
  // Don't use it for data (that's why it's returned as undefined)
  const suspenseTrigger = () => (grantsData() ? undefined : undefined);

  const all = () => GrantsIds.map((id) => grantsData.latest[id]);
  const get = (id: GrantId) => grantsData.latest[id];
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const set = (id: GrantId, grant: Grant) => mutate((prev) => ({ ...prev, [id]: grant }) as any);

  const canDelete = (id: GrantId) => get(id)?.["op.delete"] || false;
  const canUpdate = (id: GrantId) => get(id)?.["op.update"] || false;
  const canRead = (id: GrantId) => get(id)?.["op.read"] || false;
  const canCreate = (id: GrantId) => get(id)?.["op.create"] || false;

  return {
    suspenseTrigger,
    refetch,
    all,
    get,
    set,
    canDelete,
    canUpdate,
    canRead,
    canCreate,
  };
};
